import { Phone, MessageCircle, Mail, ListChecks, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@easyflow/ui-frontend/components/ui/sheet';
import type { MobileLead } from './types';

interface LeadQuickActionsSheetProps {
  lead: MobileLead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenTimeline: (lead: MobileLead) => void;
  /**
   * Internal-app handlers (mirror the desktop CRM behaviour: softphone for
   * Call, WhatsApp mini-chat modal for WhatsApp, EmailComposeModal for Email).
   * The sheet does NOT use `tel:` / `wa.me` / `mailto:` anymore — those
   * delegated to the native phone apps and bypassed the in-app tracking and
   * compliance flow that the desktop side already enforces.
   */
  onCall: (phone: string, name: string, leadId: string) => void;
  onWhatsApp: (phone: string, name: string, leadId: string) => void;
  onEmail: (email: string, name: string, leadId: string) => void;
}

export default function LeadQuickActionsSheet({
  lead,
  open,
  onOpenChange,
  onOpenTimeline,
  onCall,
  onWhatsApp,
  onEmail,
}: LeadQuickActionsSheetProps) {
  const fullName =
    [lead.contact_first_name, lead.contact_last_name].filter(Boolean).join(' ').trim() ||
    lead.title ||
    'Lead senza nome';

  const phone = lead.contact_phone?.trim();
  const email = lead.contact_email?.trim();

  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl border-t p-0 gap-0 max-h-[85vh] flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid={`mobile-lead-quick-actions-${lead.id}`}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Drag handle */}
        <div className="pt-2 pb-1 flex justify-center" aria-hidden="true">
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
        </div>

        <SheetHeader className="px-5 pt-2 pb-3 text-left space-y-1">
          <SheetTitle className="truncate text-base">{fullName}</SheetTitle>
          <SheetDescription className="truncate text-xs">
            {lead.company_name ||
              lead.contact_phone ||
              lead.contact_email ||
              'Azioni rapide lead'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col" data-testid={`mobile-lead-quick-actions-list-${lead.id}`}>
          {phone ? (
            <button
              type="button"
              onClick={() => {
                close();
                onCall(phone, fullName, lead.id);
              }}
              className="min-h-[56px] flex items-center gap-3 px-5 py-3 border-t border-border active:bg-accent/70 hover:bg-accent transition-colors text-left"
              data-testid={`mobile-lead-quick-call-${lead.id}`}
            >
              <span className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground">Chiama</span>
                <span className="block text-xs text-muted-foreground truncate">{phone}</span>
              </span>
            </button>
          ) : (
            <DisabledRow icon={<Phone className="w-5 h-5" />} label="Chiama" hint="Nessun numero" />
          )}

          {phone ? (
            <button
              type="button"
              onClick={() => {
                close();
                onWhatsApp(phone, fullName, lead.id);
              }}
              className="min-h-[56px] flex items-center gap-3 px-5 py-3 border-t border-border active:bg-accent/70 hover:bg-accent transition-colors text-left"
              data-testid={`mobile-lead-quick-wa-${lead.id}`}
            >
              <span className="w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground">WhatsApp</span>
                <span className="block text-xs text-muted-foreground truncate">{phone}</span>
              </span>
            </button>
          ) : (
            <DisabledRow
              icon={<MessageCircle className="w-5 h-5" />}
              label="WhatsApp"
              hint="Nessun numero"
            />
          )}

          {email ? (
            <button
              type="button"
              onClick={() => {
                close();
                onEmail(email, fullName, lead.id);
              }}
              className="min-h-[56px] flex items-center gap-3 px-5 py-3 border-t border-border active:bg-accent/70 hover:bg-accent transition-colors text-left"
              data-testid={`mobile-lead-quick-email-${lead.id}`}
            >
              <span className="w-10 h-10 rounded-full bg-sky-500/15 text-sky-600 dark:text-sky-400 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground">Email</span>
                <span className="block text-xs text-muted-foreground truncate">{email}</span>
              </span>
            </button>
          ) : (
            <DisabledRow icon={<Mail className="w-5 h-5" />} label="Email" hint="Nessun indirizzo" />
          )}

          <button
            type="button"
            onClick={() => {
              close();
              onOpenTimeline(lead);
            }}
            className="min-h-[56px] flex items-center gap-3 px-5 py-3 border-t border-border active:bg-accent/70 hover:bg-accent transition-colors text-left"
            data-testid={`mobile-lead-quick-timeline-${lead.id}`}
          >
            <span className="w-10 h-10 rounded-full bg-muted text-foreground flex items-center justify-center shrink-0">
              <ListChecks className="w-5 h-5" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-foreground">Apri timeline</span>
              <span className="block text-xs text-muted-foreground truncate">
                Dettagli, attività e note
              </span>
            </span>
          </button>
        </div>

        <SheetClose asChild>
          <button
            type="button"
            className="mx-5 my-3 min-h-[44px] rounded-lg border border-border text-sm font-medium text-foreground active:bg-accent/70 hover:bg-accent transition-colors flex items-center justify-center gap-2"
            data-testid={`mobile-lead-quick-cancel-${lead.id}`}
          >
            <X className="w-4 h-4" /> Annulla
          </button>
        </SheetClose>
      </SheetContent>
    </Sheet>
  );
}

function DisabledRow({
  icon,
  label,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <div
      className="min-h-[56px] flex items-center gap-3 px-5 py-3 border-t border-border opacity-50 cursor-not-allowed"
      aria-disabled="true"
    >
      <span className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-foreground">{label}</span>
        <span className="block text-xs text-muted-foreground truncate">{hint}</span>
      </span>
    </div>
  );
}
